import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, UserPlus, Unlink, AlertTriangle, MapPin } from 'lucide-react'
import { apiGet, cachedGet, postJson, patchJson } from '../../lib/api.js'
import SearchableSelect from '../SearchableSelect.jsx'
import ConfirmDialog from '../ConfirmDialog.jsx'
import { nameInitial } from '../../lib/text.js'
import { fullName } from '../../lib/employee.js'
import { relabelBranch } from '../../lib/asset.js'
import { calendarFor } from '../../lib/employeeSchedule.js'
import { branchWindow, timeOnDay } from '../../lib/doctorSchedule.js'
import {
  ASSIST_SHIFT_TYPE, assistMatch, isAssistant, isLinkedTo, linksOf, matchNote,
  rosterByEmployee, withLink, withoutLink,
} from '../../lib/shiftAssist.js'

/**
 * ผู้ช่วยประจำเวร — which dental assistants are attached to THIS doctor shift.
 *
 * The attachment is not a new table: it is the assistant's own
 * `employee_timetable` row for that day, carrying `doctor_id` + this shift's id
 * in `appointment_id[]` (see lib/shiftAssist.js). That has two consequences the
 * UI has to be honest about:
 *
 *  · An assistant who ALREADY has a shift that day is LINKED, not re-rostered —
 *    Xano allows one active row per (employee, date), and their existing row is
 *    the one payroll counts. Their window and branch are left exactly as they
 *    are; if that branch is not this shift's branch, the row says so instead of
 *    quietly moving them.
 *  · An assistant with no shift that day gets one created here, on the branch's
 *    own clock-in→clock-out hours (`เวรผู้ช่วย`) — the same row the พนักงาน tab
 *    would have made.
 *
 * Removing only unlinks. Deleting the shift itself is the พนักงาน tab's job:
 * the row is that person's attendance and pay for the day, and nothing about
 * "this assistant is not with this doctor" says they did not work.
 *
 * Writes land immediately (they touch other rows), so this section is only
 * offered once the shift itself exists.
 */
export default function ShiftAssistants({ shift, spec, exceptIds, appt, onChanged }) {
  const shiftId = Number(shift?.id) || 0
  const dateKey = String(shift?.date || '').slice(0, 10)
  const branchId = Number(shift?.branch_id) || 0
  const doctorId = Number(shift?.doctor_id) || 0

  const [employees, setEmployees] = useState([])
  const [branches, setBranches] = useState([])
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const [confirmRow, setConfirmRow] = useState(null)
  const [tick, setTick] = useState(0)
  const reqId = useRef(0)

  useEffect(() => {
    cachedGet('/api/hr/employees', { per_page: 500, active: 'true' })
      .then((r) => setEmployees((Array.isArray(r) ? r : (r?.items || [])).filter(isAssistant)))
      .catch(() => {})
    cachedGet('/api/branches')
      .then((r) => setBranches(Array.isArray(r) ? r : (r?.items || []))).catch(() => {})
  }, [])

  // The whole clinic's roster for that ONE day, not just this branch: the
  // one-row-per-day rule is global, so an assistant already working elsewhere
  // must be visible here or the create would fail with a puzzling 400.
  useEffect(() => {
    if (!dateKey) { setRows([]); setLoading(false); return }
    const id = ++reqId.current
    setLoading(true); setErr('')
    apiGet('/api/hr/schedule/employee', { start_date: dateKey, end_date: dateKey, per_page: 300 })
      .then((r) => { if (id === reqId.current) setRows(Array.isArray(r) ? r : (r?.items || [])) })
      .catch((e) => { if (id === reqId.current) { setRows([]); setErr(String(e.message || e)) } })
      .finally(() => { if (id === reqId.current) setLoading(false) })
  }, [dateKey, tick])

  const byEmployee = useMemo(() => rosterByEmployee(rows), [rows])
  const empById = useMemo(() => {
    const m = new Map()
    for (const e of employees) m.set(Number(e.id), e)
    return m
  }, [employees])
  const branchName = useMemo(() => {
    const m = new Map()
    for (const b of branches) m.set(Number(b.id), relabelBranch(b.name))
    return m
  }, [branches])

  const linked = useMemo(
    () => rows.filter((r) => isLinkedTo(r, shiftId)).sort((a, b) => String(a.employee?.code || '').localeCompare(String(b.employee?.code || ''), 'th')),
    [rows, shiftId],
  )
  const linkedIds = useMemo(() => new Set(linked.map((r) => Number(r.employee_id))), [linked])

  // Every assistant, scored against this shift's effective spec/except.
  const scored = useMemo(() => employees
    .filter((e) => !linkedIds.has(Number(e.id)))
    .map((e) => ({ emp: e, match: assistMatch(e, { spec, exceptIds }, appt) }))
    .sort((a, b) => (Number(b.match.ok) - Number(a.match.ok))
      || String(a.emp.code || '').localeCompare(String(b.emp.code || ''), 'th')),
  [employees, linkedIds, spec, exceptIds, appt])

  const offCount = scored.filter((s) => !s.match.ok).length

  const options = useMemo(() => scored
    .filter((s) => showAll || s.match.ok)
    .map(({ emp, match }) => {
      const day = byEmployee.get(Number(emp.id))
      const elsewhere = day && Number(day.branch_id) !== branchId
      const note = match.ok ? '' : matchNote(match, appt?.apptById)
      return {
        value: String(emp.id),
        label: emp.code || fullName(emp),
        hint: [
          fullName(emp),
          day ? `มีเวร ${day.type}${elsewhere ? ` · ${branchName.get(Number(day.branch_id)) || `สาขา #${day.branch_id}`}` : ''}` : 'ยังไม่มีเวรวันนี้',
          note,
        ].filter(Boolean).join(' · '),
      }
    }), [scored, showAll, byEmployee, branchId, branchName, appt])

  async function attach(empId) {
    const id = Number(empId) || 0
    if (!id || busy) return
    setBusy(true); setErr('')
    try {
      const day = byEmployee.get(id)
      if (day) {
        // They are already rostered — link, never re-roster. Their window,
        // branch and travel fee are none of this drawer's business.
        await patchJson(`/api/hr/schedule/employee/${day.id}`, {
          doctor_id: doctorId,
          appointment_id: withLink(day, shiftId),
        })
      } else {
        const branch = branches.find((b) => Number(b.id) === branchId)
        const w = branchWindow(branch)
        await postJson('/api/hr/schedule/employee', {
          employee_id: id,
          branch_id: branchId,
          type: ASSIST_SHIFT_TYPE,
          date: dateKey,
          start_date: timeOnDay(dateKey, w.start),
          end_date: timeOnDay(dateKey, w.end),
          travel_fee: false,
          travel_fee_amount: 0,
          remark: '',
          doctor_id: doctorId,
          appointment_id: [shiftId],
          calendar: calendarFor(ASSIST_SHIFT_TYPE),
        })
      }
      setTick((t) => t + 1); onChanged?.()
    } catch (e) { setErr(String(e.message || e)) }
    finally { setBusy(false) }
  }

  async function detach(row) {
    setConfirmRow(null)
    setBusy(true); setErr('')
    try {
      const rest = withoutLink(row, shiftId)
      await patchJson(`/api/hr/schedule/employee/${row.id}`, {
        appointment_id: rest,
        // No shift left to assist → drop the doctor too, or the row keeps
        // pointing at a doctor it is no longer attached to.
        ...(rest.length ? {} : { doctor_id: 0 }),
      })
      setTick((t) => t + 1); onChanged?.()
    } catch (e) { setErr(String(e.message || e)) }
    finally { setBusy(false) }
  }

  return (
    <div>
      {loading ? (
        <div className="flex items-center gap-1.5 text-[12.5px] text-slate-400"><Loader2 size={13} className="animate-spin" /> กำลังโหลดเวรผู้ช่วย…</div>
      ) : (
        <div className="space-y-1.5">
          {!linked.length && <div className="text-[12.5px] text-slate-400">ยังไม่มีผู้ช่วยประจำเวรนี้</div>}
          {linked.map((r) => {
            const emp = empById.get(Number(r.employee_id))
            const elsewhere = Number(r.branch_id) !== branchId
            const name = r.employee?.code || emp?.code || fullName(emp) || `#${r.employee_id}`
            const m = emp ? assistMatch(emp, { spec, exceptIds }, appt) : { ok: true, missing: [], refused: [] }
            return (
              <div key={r.id} className="flex items-center gap-2.5 rounded-xl ring-1 ring-slate-200 bg-white px-3 py-2">
                <span className="w-7 h-7 rounded-lg bg-brand-50 text-brand-700 ring-1 ring-brand-100 flex items-center justify-center text-[12px] font-semibold shrink-0">
                  {nameInitial(name)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium text-slate-800 truncate">{name}</div>
                  <div className="text-[11.5px] text-slate-400 truncate">
                    {r.type}
                    {elsewhere && <> · <MapPin size={10} className="inline -mt-0.5" /> {branchName.get(Number(r.branch_id)) || `สาขา #${r.branch_id}`}</>}
                    {!m.ok && <> · <span className="text-amber-600">{matchNote(m, appt?.apptById)}</span></>}
                    {linksOf(r).length > 1 && <> · ผูกกับ {linksOf(r).length} เวร</>}
                  </div>
                </div>
                <button type="button" onClick={() => setConfirmRow(r)} disabled={busy}
                  className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-[12.5px] text-slate-500 hover:bg-slate-100 disabled:opacity-50 shrink-0" title="ปลดออกจากเวรนี้">
                  <Unlink size={13} /> ปลด
                </button>
              </div>
            )
          })}
        </div>
      )}

      <div className="mt-2.5">
        <SearchableSelect
          options={options}
          value=""
          onChange={attach}
          disabled={busy || !shiftId}
          placeholder="เพิ่มผู้ช่วย…"
          searchPlaceholder="พิมพ์ชื่อผู้ช่วย…"
          emptyText={showAll ? 'ไม่มีผู้ช่วยให้เลือก' : 'ไม่มีผู้ช่วยที่ตรงความเชี่ยวชาญของหมอ'}
        />
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <span className="text-[11.5px] text-slate-400 inline-flex items-center gap-1">
            <UserPlus size={12} /> เลือกได้เฉพาะพนักงานที่มีบทบาท Assistance
            {!showAll && <> · ตรงกับความเชี่ยวชาญของหมอ {options.length} คน</>}
          </span>
          {offCount > 0 && (
            <button type="button" onClick={() => setShowAll((v) => !v)}
              className="text-[11.5px] text-brand-600 hover:underline shrink-0">
              {showAll ? 'แสดงเฉพาะที่ตรงเงื่อนไข' : `แสดงนอกเงื่อนไขด้วย (${offCount})`}
            </button>
          )}
        </div>
      </div>

      {err && (
        <div className="mt-2 flex items-start gap-1.5 text-[12.5px] text-rose-600">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {err}
        </div>
      )}

      {confirmRow && (
        <ConfirmDialog
          title="ปลดผู้ช่วยออกจากเวรนี้"
          message="ระบบจะปลดการผูกกับเวรของหมอเท่านั้น — เวรของผู้ช่วยในวันนั้นยังอยู่ครบ (ลบเวรได้ที่แท็บ พนักงาน)"
          confirmLabel="ปลดออก"
          onConfirm={() => detach(confirmRow)}
          onCancel={() => setConfirmRow(null)}
        />
      )}
    </div>
  )
}
